import { CalendarClock, MailCheck, Loader2 } from "lucide-react";
import { fmtDate } from "../proposal/format";

// Shown under the deadline field when the client's date leaves fewer
// working days than the proposal quoted. The client asks Medicys to
// check; Medicys approves in the viewer (the note turns green) or
// refuses silently and offers a workable date by email, which the
// client enters above.
// True while the client's deadline is short and Medicys has not yet
// confirmed it: the sections below the deadline stay hidden.
export const deadlineBlocks = (a, deadline) =>
  !!deadline && !!a?.project_deadline && deadline.deadline === a.project_deadline && !!deadline.short && !deadline.confirmed;

export function DeadlineNote({ a, deadline, onCheck, checking }) {
  if (!deadline || !a.project_deadline || deadline.deadline !== a.project_deadline) return null;
  const { quoted_working_days: quoted, available_working_days: available, requested_at: requestedAt, short, confirmed, counted_from: countedFrom } = deadline;
  if (!short) return null;
  const state = confirmed ? "confirmed" : requestedAt ? "requested" : "short";
  const tone = confirmed
    ? ["border-emerald-200 bg-emerald-50/70 text-emerald-950", "bg-emerald-600"]
    : requestedAt
      ? ["border-sky-200 bg-sky-50/70 text-sky-950", "bg-[#1B4F8A]"]
      : ["border-amber-200 bg-amber-50/70 text-amber-950", "bg-amber-500"];
  return (
    <div
      className={`rounded-lg border p-4 sm:p-5 flex flex-col gap-4 text-[15px] leading-relaxed ${tone[0]}`}
      data-testid="cm-deadline-note"
      data-state={state}
    >
      <div className="flex items-start gap-3">
        <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-white ${tone[1]}`}>
          {state === "short" ? <CalendarClock className="w-4 h-4" /> : <MailCheck className="w-4 h-4" />}
        </span>
        <div className="flex flex-col gap-2">
          <p>
            Your deadline of <span className="font-semibold">{fmtDate(a.project_deadline)}</span> leaves <span className="font-semibold">{available} working {available === 1 ? "day" : "days"}</span>{countedFrom ? <> from your planned start of {fmtDate(countedFrom)}</> : null}; the proposal is based on <span className="font-semibold">{quoted} working days</span>.
          </p>
          {state === "short" && (
            <p data-testid="cm-deadline-short">
              Commissioning cannot proceed until Medicys has confirmed that this deadline is workable. Click below for a feasibility assessment and we will come back to you by email, or enter a later deadline above to continue straight away.
            </p>
          )}
          {state === "requested" && (
            <p data-testid="cm-deadline-requested">
              Thank you. Your request was sent to Medicys on {fmtDate(requestedAt)}. We will assess the feasibility of this deadline and be in touch by email. You can close this window now. When you return, you will be able to continue as soon as Medicys has confirmed the deadline here, or you can enter a later deadline above at any time.
            </p>
          )}
          {state === "confirmed" && <p data-testid="cm-deadline-confirmed">Medicys has confirmed that this deadline is workable. You can continue.</p>}
        </div>
      </div>
      {state === "short" && onCheck && (
        <button
          type="button"
          onClick={onCheck}
          disabled={checking}
          data-testid="cm-deadline-check"
          className="self-start inline-flex items-center gap-2 rounded-md bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white font-semibold px-4 py-2.5 text-sm transition-colors"
        >
          {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <MailCheck className="w-4 h-4" />} Ask Medicys to check this deadline
        </button>
      )}
    </div>
  );
}
